import type { ComponentType } from "react";
import { tv } from "tailwind-variants";

import { useTheme } from "@/hooks/use-theme";
import type { ThemePreference } from "@/stores/use-theme-store";
import type { SVGProps } from "@/ui";
import { focusRing, icons, RadioGroup } from "@/ui";

const appearanceVariants = tv({
  slots: {
    option: [
      "flex cursor-pointer items-center gap-4 rounded-card border p-4 transition-colors",
      "hover:border-focus hover:bg-surface-hover",
      focusRing,
    ],
    iconWrapper: "flex size-11 shrink-0 items-center justify-center rounded-full transition-colors",
  },
  variants: {
    selected: {
      true: {
        option: "border-focus bg-accent-surface",
        iconWrapper: "bg-accent text-accent-content",
      },
      false: {
        option: "border-line bg-surface",
        iconWrapper: "bg-surface-sunken text-content-muted",
      },
    },
  },
});

const { iconWrapper, option } = appearanceVariants();

type AppearanceOption = {
  value: ThemePreference;
  label: string;
  description: string;
  icon: ComponentType<SVGProps>;
};

const OPTIONS: AppearanceOption[] = [
  {
    value: "system",
    label: "Match my device",
    description: "Follows your phone or computer's own light and dark setting.",
    icon: icons.ComputerDesktopIcon,
  },
  {
    value: "light",
    label: "Light",
    description: "Always use the light theme, whatever your device is set to.",
    icon: icons.SunIcon,
  },
  {
    value: "dark",
    label: "Dark",
    description: "Always use the dark theme, easier on the eyes at night.",
    icon: icons.MoonIcon,
  },
];

export const AppearanceSettings = () => {
  const { preference, resolvedTheme, setPreference } = useTheme();

  return (
    <div className="mt-6 flex flex-col gap-6 text-content sm:mt-0">
      <h2 className="hidden font-semibold sm:block">Appearance</h2>

      <p className="text-content-secondary">
        Choose how My Care Plan looks. By default it matches your device, so it turns dark when your
        phone does — pick Light or Dark below to override that.
      </p>

      <RadioGroup.Root
        aria-label="Appearance"
        className="gap-3"
        onValueChange={(value) => {
          setPreference(value as ThemePreference);
        }}
        value={preference}
      >
        {OPTIONS.map(({ description, icon: Icon, label, value }) => {
          const selected = preference === value;
          const isActiveSystemTheme = value === "system" && selected;

          return (
            <label className={option({ selected })} key={value}>
              <span aria-hidden="true" className={iconWrapper({ selected })}>
                <Icon className="size-6" />
              </span>

              <span className="flex grow flex-col gap-0.5">
                <span className="font-semibold">
                  {label}
                  {isActiveSystemTheme ? (
                    <span className="font-normal text-content-muted">
                      {` — currently ${resolvedTheme}`}
                    </span>
                  ) : null}
                </span>
                <span className="text-sm text-content-secondary">{description}</span>
              </span>

              <RadioGroup.Item className="shrink-0" value={value}>
                <RadioGroup.Indicator />
              </RadioGroup.Item>
            </label>
          );
        })}
      </RadioGroup.Root>
    </div>
  );
};
